import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, PagingSidePanelType, SidePanel } from '@node-space/ui-components'
import { capitalize, ellipseMiddleText } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { ContactInstrument, InstrumentType } from 'types/contacts'
import { highlightRowBasedOnId } from 'utils/table/table'
import { ContactInstrumentsSidePanelBody } from './ContactInstrumentsSidePanelBody'

export interface ContactInstrumentsSidePanelProps {
  isOpen: boolean
  contactInstrument: ContactInstrument
  pagingInstruments: PagingSidePanelType
  onClose: () => void
}

export const ContactInstrumentsSidePanel = ({
  isOpen,
  contactInstrument,
  pagingInstruments,
  onClose,
}: ContactInstrumentsSidePanelProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { description, currency, accountNumber, network } = contactInstrument || {}

  const isCryptoInstrument = contactInstrument?.type === InstrumentType.CRYPTO

  const iconUrl = currency
    ? `${process.env.ICONS_URL}${currency?.toLowerCase()}.svg`
    : process.env.ICONS_URL

  const primaryLabel = useMemo(() => {
    if (!contactInstrument || !description) return

    if (isCryptoInstrument) {
      return description
    }

    return description || t('contacts.bankAccount')
  }, [contactInstrument, description, isCryptoInstrument, t])

  const secondaryLabel = useMemo(() => {
    if (!contactInstrument || !currency) return

    if (isCryptoInstrument) {
      return `${currency} (${capitalize(network)})`
    }

    return `${currency} ${t('contacts.bankAccount')}`
  }, [contactInstrument, currency, network, isCryptoInstrument, t])

  return (
    <SidePanel
      id="side-panel-contact-instrument"
      isOpen={isOpen}
      paging={pagingInstruments}
      onClose={onClose}
      activeRowId={contactInstrument?.id}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <SidePanelHeaderTemplateTwo
          heading={{
            iconUrls: [iconUrl],
            primaryLabel: (
              <Box className="md:flex w-full">
                <h2 className="text-foreground-prominent text-h2">{primaryLabel}</h2>
              </Box>
            ),
            secondaryLabel: (
              <p className="text-body-regular text-foreground-prominent">{secondaryLabel}</p>
            ),
          }}
          bottomContent={{
            leftLabel: t('accountNumber'),
            leftDescription: !isCryptoInstrument && accountNumber && (
              <p className="text-foreground-prominent text-body-regular">
                {accountNumber.length > 30
                  ? ellipseMiddleText(accountNumber, 10, 10)
                  : accountNumber}
              </p>
            ),
          }}
        />
      }
      body={
        <Box tag="section" width="full" background="standard">
          <ContactInstrumentsSidePanelBody contactInstrument={contactInstrument} />
        </Box>
      }
    />
  )
}
